import Close from '@betfan/icons/linear/EssentionalUI/Close.svg?react';
import NotificationBellDark from 'assets/animations/NotificationBellDark.json';
import NotificationBellLight from 'assets/animations/NotificationBellLight.json';
import { DSSwitch } from 'designSystem';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timeout } from 'react-number-format/types/types';
import { useSelector } from 'react-redux';
import { AppTheme, NOTIFICATION_PERMISSION_RETRY_PERIOD_MINUTES } from 'shared/constants/common';
import { setDataLayer } from 'shared/constants/datalayer';
import useClickOutside from 'shared/hooks/useClickOutside';
import useResolvedColorScheme from 'shared/hooks/useResolvedColorScheme';
import { State } from 'shared/interface';
import { isLoggedin } from 'shared/util/utility';

const Player = lazy(() => import('@lottiefiles/react-lottie-player').then((module) => ({ default: module.Player })));

const isNotificationSupported = typeof Notification !== 'undefined' && Notification.permission !== undefined;

export const getNotificationPermission = () => (isNotificationSupported ? Notification.permission === 'granted' : false);

export const NativeNotificationManager = () => {
	const { t } = useTranslation();
	const { resolvedColorScheme } = useResolvedColorScheme();
	const { userId } = useSelector<State, any>((state) => state.login.userDetails);

	const getNotificationData = () => {
		const notificationData = localStorage.getItem('notificationData');
		return notificationData ? JSON.parse(notificationData) : { userClosedNotification: false, date: new Date() };
	};
	const notificationRef = useClickOutside(() => closeModal(), 'click');
	const [modalClosed, setModalClosed] = useState(getNotificationData().userClosedNotification);
	const [isNotificationChecked, setIsNotificationChecked] = useState<boolean>(false);
	const timer = useRef<Timeout | null>(null);
	const shouldRenderModal = isLoggedin() && !modalClosed;
	const notificationAnimation = resolvedColorScheme === AppTheme.DARK ? NotificationBellDark : NotificationBellLight;
	const notificationPermission = isNotificationSupported ? Notification.permission : 'default';

	const checkNotificationStatus = useCallback(() => {
		const { userClosedNotification, date } = getNotificationData();
		const currentDate = new Date();

		const savedDate = isValidDate(date) ? new Date(date) : new Date();
		const minuteDifference = (currentDate.getTime() - savedDate.getTime()) / 1000 / 60;

		if (notificationPermission !== 'granted') {
			if (minuteDifference >= NOTIFICATION_PERMISSION_RETRY_PERIOD_MINUTES) {
				optimoveSend();
				setModalClosed(false);
				setLocalStorageNotificationData(false);
			} else if (userClosedNotification) {
				optimoveSignOut();
			}
		} else {
			if (userClosedNotification) {
				optimoveSend();
			} else {
				setModalClosed(true);
				setLocalStorageNotificationData(true);
				optimoveSend();
			}
		}
	}, [notificationPermission]);

	const optimoveSend = () => {
		window?.optimoveSDK?.API.setUserId(userId.toString());
	};

	const optimoveSignOut = () => {
		window?.optimoveSDK?.API.signOutUser();
	};

	const isValidDate = (dateString: string) => {
		const date = new Date(dateString);
		return !isNaN(date.getTime());
	};

	const toggleNotification = async () => {
		setIsNotificationChecked(!isNotificationChecked);
		await Notification.requestPermission();
		closeModal();
	};

	const setLocalStorageNotificationData = (userClosedNotification: boolean) => {
		localStorage.setItem('notificationData', JSON.stringify({ userClosedNotification, date: new Date() }));
	};

	const closeModal = () => {
		setDataLayer({
			event: 'notification-status',
			notificationsEnabled: getNotificationPermission(),
		});

		setModalClosed(true);
		setLocalStorageNotificationData(true);
	};

	const detectOptimove = useCallback(() => {
		if (window.optimoveSDK !== undefined) {
			checkNotificationStatus();
		}
	}, [checkNotificationStatus]);

	useEffect(() => {
		if (!isLoggedin()) return;

		timer.current = setInterval(() => {
			checkNotificationStatus();
			detectOptimove();
		}, 1000);

		return () => {
			if (timer.current) {
				clearInterval(timer.current);
			}
		};
	}, [notificationPermission, checkNotificationStatus, detectOptimove]);

	if (!isNotificationSupported || !shouldRenderModal) return <></>;

	return (
		<>
			<div
				ref={notificationRef}
				onClick={() => closeModal()}
				className="lg:h-full h-screen fixed w-full backdrop-blur bg-black bg-opacity-60 z-notification-modal overflow-hidden"
			>
				<div
					onClick={(e) => e.stopPropagation()}
					className="fixed bottom-panel-offset lg:transform-none lg:top-[100px] lg:bottom-auto lg:left-10  w-[calc(100%-16px)] lg:w-[374px] left-1/2 -translate-x-1/2 pb-8 pt-7 px-3 flex items-center flex-col justify-center rounded-16 bg-white dark:bg-dark-200"
				>
					<button
						className="absolute w-10 h-10 flex items-center justify-center top-0 right-0"
						onClick={() => {
							closeModal();
						}}
					>
						<Close className="w-7 h-7" />
					</button>
					<Suspense fallback={<></>}>
						<Player src={notificationAnimation} autoplay loop />
					</Suspense>
					<div className="mt-6 font-bold text-h3 text-gray-900 dark:text-gray-100 mb-4">{t('notificationTitle')}</div>
					<DSSwitch label={t('notificationSwitch')} checked={isNotificationChecked} onChange={() => toggleNotification()} />
				</div>
			</div>
		</>
	);
};
